import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { authApi } from "@/services/api";

type User = {
  username?: string;
  name?: string;
  role?: string;
  email: string;
  password?: string; // Only used during login/signup, not stored
  mobile?: string;
  mobileNo?: string;
  isAuthenticated: boolean;
  token?: string; // JWT token from the API
  userId?: string; // User ID from the API
  organizationId?: string; // Organization ID from the API
};

type Organization = {
  name: string;
  buildingName?: string;
  streetName?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  contactNo?: string;
  subDivisionName?: string;
  logo?: string;
  location?: string;
  email?: string;
  website?: string;
  companyRegNo?: string;
  taxRegNo?: string;
};

type SignupData = {
  user: User;
  organization: Organization;
};

type AuthContextType = {
  user: User | null;
  login: (email: string, password: string) => Promise<any>;
  googleSignIn: (email: string) => Promise<any>;
  googleSignUp: (email: string, name: string) => Promise<any>;
  logout: () => void;
  signup: (data: SignupData) => Promise<any>;
  isAuthenticated: boolean;
};

const initialAuthContext: AuthContextType = {
  user: null,
  login: async () => {
    return Promise.resolve(null);
  },
  googleSignIn: async () => {
    return Promise.resolve(null);
  },
  googleSignUp: async () => {
    return Promise.resolve(null);
  },
  logout: () => {},
  signup: async () => {
    return Promise.resolve(null);
  },
  isAuthenticated: false,
};

const AuthContext = createContext<AuthContextType>(initialAuthContext);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Check if user data exists in localStorage
    const storedUser = localStorage.getItem("manterflow-user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsAuthenticated(parsedUser.isAuthenticated);
      } catch (error) {
        console.error("Error parsing stored user:", error);
        localStorage.removeItem("manterflow-user");
      }
    }
    setIsInitialized(true);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      // Call the API to authenticate the user
      const response = await authApi.login(email, password);

      console.log("Login response:", response);

      // Check for success response with the correct format
      if (response && response.data) {
        // Get profile data from response
        const profile = response.data.profile || {};

        // Create a user object from the API response
        const userData: User = {
          email: profile.email || email,
          role: profile.role || "user",
          isAuthenticated: true,
          token: response.data.token,
          name: profile.name,
          userId: profile.id,
          organizationId: profile.organizationId,
          mobile: profile.mobile,
        };

        // Store in localStorage first
        localStorage.setItem("manterflow-user", JSON.stringify(userData));

        // Store token separately for easy access
        if (response.data.token) {
          localStorage.setItem("manterflow-token", response.data.token);
        }

        // Store profile data separately
        if (response.data.profile) {
          localStorage.setItem(
            "manterflow-user-profile",
            JSON.stringify(response.data.profile)
          );
        }

        // Then update state
        setUser(userData);
        setIsAuthenticated(true);
        return response;
      }

      return response;
    } catch (error) {
      console.log(error, "AUTH CONTEXT");
      console.error("Error during login:", error);
      throw error;
    }
  };

  const logout = () => {
    try {
      localStorage.removeItem("manterflow-user");
      localStorage.removeItem("manterflow-organization");
      localStorage.removeItem("manterflow-token");
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error("Error during logout:", error);
      throw error;
    }
  };

  const signup = async (data: SignupData) => {
    try {
      // Call the API to register the user and organization
      const response = await authApi.signup(
        {
          name: data.user.name || data.user.username,
          email: data.user.email,
          password: data.user.password,
          mobile: data.user.mobileNo,
        },
        {
          name: data.organization.name,
          buildingName: data.organization.buildingName,
          streetName: data.organization.streetName,
          city: data.organization.city,
          state: data.organization.state,
          country: data.organization.country,
          postalCode: data.organization.postalCode,
          contactNo: data.organization.contactNo,
        }
      );

      // Check for success response with the correct format
      if (response && response.status === "success" && response.data) {
        console.log(response, "SIGNUP RESPONSE");

        // Ensure user data exists
        if (!response.data.user) {
          throw new Error("User data not found in signup response");
        }

        // Create a user object from the API response
        const userData: User = {
          email: data.user.email || "",
          name: data.user.name || data.user.username,
          role: response.data.user.role || "user",
          isAuthenticated: true,
          token: response.data.token,
          userId: response.data.user.id,
          organizationId: response.data.user.organizationId,
        };

        // Store user data
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem("manterflow-user", JSON.stringify(userData));

        // Store token separately for easy access
        if (response.data.token) {
          localStorage.setItem("manterflow-token", response.data.token);
        }

        // Store organization data if returned by API
        if (response.data.organization) {
          localStorage.setItem(
            "manterflow-organization",
            JSON.stringify(response.data.organization)
          );
        }

        return response;
      }

      throw new Error("Invalid signup response format");
    } catch (error) {
      console.error("Error during signup:", error);
      throw error;
    }
  };

  const googleSignIn = async (email: string) => {
    try {
      // Call the Google sign-in API
      const response = await authApi.googleSignIn(email);

      console.log("Google sign-in response:", response);

      // Check for success response with the correct format
      if (response && response.data) {
        // Ensure user data exists
        if (!response.data.profile) {
          throw new Error("User data not found in Google sign-in response");
        }

        // Create a user object from the API response
        const userData: User = {
          email: response.data.profile.email || email,
          name: response.data.profile.name || response.data.profile.username,
          role: response.data.profile.role || "user",
          isAuthenticated: true,
          token: response.data.token,
          userId: response.data.profile.id,
          organizationId: response.data.profile.organizationId,
        };

        // Store user data in localStorage
        localStorage.setItem("manterflow-user", JSON.stringify(userData));
        localStorage.setItem("manterflow-token", response.data.token);

        // Store organization data if available
        if (response.data.organization) {
          localStorage.setItem(
            "manterflow-organization",
            JSON.stringify(response.data.organization)
          );
        }

        // Update state
        setUser(userData);
        setIsAuthenticated(true);

        return response;
      }

      throw new Error("Invalid Google sign-in response format");
    } catch (error) {
      console.error("Error during Google sign-in:", error);
      throw error;
    }
  };

  const googleSignUp = async (email: string, name: string) => {
    try {
      // Call the Google sign-up API
      const response = await authApi.googleSignUp(email, name);

      console.log("Google sign-up response:", response);

      // Check for success response with the correct format
      if (response && response.data) {
        // Ensure user data exists
        if (!response.data.user) {
          throw new Error("User data not found in Google sign-up response");
        }

        // Create a user object from the API response
        const userData: User = {
          email: response.data.user.email || email,
          name: response.data.user.name || name,
          role: response.data.user.role || "user",
          isAuthenticated: true,
          token: response.data.token,
          userId: response.data.user.id,
          organizationId: response.data.user.organizationId,
        };

        // Store user data in localStorage
        localStorage.setItem("manterflow-user", JSON.stringify(userData));
        localStorage.setItem("manterflow-token", response.data.token);

        // Store organization data if available
        if (response.data.organization) {
          localStorage.setItem(
            "manterflow-organization",
            JSON.stringify(response.data.organization)
          );
        }

        // Update state
        setUser(userData);
        setIsAuthenticated(true);

        return response;
      }

      throw new Error("Invalid Google sign-up response format");
    } catch (error) {
      console.error("Error during Google sign-up:", error);
      throw error;
    }
  };

  // Don't render children until initialization is complete
  if (!isInitialized) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{ user, login, googleSignIn, googleSignUp, logout, signup, isAuthenticated }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
